import { Card, Readout } from '@saniplus/ui';
import { useTranslation } from 'react-i18next';

import { clockParts, secondsOf } from '../lib/format';
import { PanelHeading } from './FactList';

/**
 * « HEURE RETENUE » — the forty seconds between what the phone showed and what
 * the server recorded, named.
 *
 * This is **not** a `punch_adjustment` and is never labelled as one. It is the
 * difference between the optimistic local display and the server's
 * `recordedAt`, and it borrows the adjustment ledger's old → new form because
 * that form already means *"the value you saw is not the value that counts"* —
 * which is exactly the message (`W3-home-states/STATES.md` §8).
 *
 * The device time is struck through in muted ink; the server time is the one
 * rendered at weight, because it is the one that gets paid. When the two agree
 * the strike-through is omitted rather than drawn against itself.
 */
interface ReconciliationPanelProps {
  /** The device instant the punch was captured at. */
  readonly deviceRecordedAt: string | null;
  /** The instant the server actually recorded. */
  readonly serverRecordedAt: string;
  readonly timezone: string;
  /** Amendment #1: the punch was already recorded and this is the replay. */
  readonly idempotentReplay?: boolean;
  readonly className?: string;
}

export function ReconciliationPanel({
  deviceRecordedAt,
  serverRecordedAt,
  timezone,
  idempotentReplay = false,
  className,
}: ReconciliationPanelProps) {
  const { t } = useTranslation('punch');

  const device = deviceRecordedAt === null ? null : clockParts(deviceRecordedAt, timezone);
  const server = clockParts(serverRecordedAt, timezone);
  const driftMs =
    deviceRecordedAt === null ? 0 : Date.parse(serverRecordedAt) - Date.parse(deviceRecordedAt);
  const seconds = Number.isNaN(driftMs) ? 0 : secondsOf(driftMs);
  const drifted = seconds > 0;

  const sentence = idempotentReplay
    ? t('reconciliation.replay')
    : !drifted
      ? t('reconciliation.same')
      : driftMs > 0
        ? t('reconciliation.behind', { seconds })
        : t('reconciliation.ahead', { seconds });

  return (
    <Card
      padding="phone"
      label={t('reconciliation.title')}
      {...(className === undefined ? {} : { className })}
    >
      <PanelHeading
        title={t('reconciliation.title')}
        chip={{ tone: 'success', label: t('reconciliation.confirmed') }}
      />

      {device !== null && drifted ? (
        <div className="mt-2.5 flex items-baseline justify-between gap-3">
          <span className="text-body-compact text-ink-muted">
            {t('reconciliation.onYourPhone')}
          </span>
          <Readout
            scale="data"
            tone="muted"
            className="line-through"
            {...(deviceRecordedAt === null ? {} : { dateTime: deviceRecordedAt })}
          >
            {t('format.clock', device)}
          </Readout>
        </div>
      ) : null}

      <div className="mt-1.5 flex items-baseline justify-between gap-3">
        <span className="text-body-compact font-semibold text-ink">
          {t('reconciliation.serverTime')}
        </span>
        <Readout scale="detail" dateTime={serverRecordedAt} className="text-ink">
          {server === null ? '' : t('format.clock', server)}
        </Readout>
      </div>

      <p className="mt-2.5 border-t border-hairline pt-2.5 text-body text-ink-muted">
        {sentence}
      </p>
    </Card>
  );
}
